/**
 * NotificationPanel Component
 *
 * Dropdown panel displaying the list of notifications with grouping by date.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { formatRelativeTime, parseAsUTC } from '@/utils/format';
import {
  useNotifications,
  useMarkRead,
  useMarkAllRead,
  useDismissNotification,
} from '@/hooks/useNotifications';
import { NotificationIcon, BellIcon, XMarkIcon, CheckDoubleIcon } from './notificationIcons';
import Spinner from '@/components/ui/Spinner';
import type { Notification } from '@/services/notifications';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if two dates are the same day
 */
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Check if a date is yesterday relative to today
 */
function isYesterday(date: Date, today: Date): boolean {
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  return isSameDay(date, yesterday);
}

/**
 * Group notifications by date (Today, Yesterday, Earlier)
 */
function groupByDate(notifications: Notification[]): Map<string, Notification[]> {
  const groups = new Map<string, Notification[]>();
  const today = new Date();

  for (const notification of notifications) {
    const date = parseAsUTC(notification.createdAt);
    let groupKey: string;

    if (isSameDay(date, today)) {
      groupKey = 'Today';
    } else if (isYesterday(date, today)) {
      groupKey = 'Yesterday';
    } else {
      groupKey = 'Earlier';
    }

    const existing = groups.get(groupKey) ?? [];
    existing.push(notification);
    groups.set(groupKey, existing);
  }

  return groups;
}

// ============================================================================
// Component Props
// ============================================================================

interface NotificationPanelProps {
  onClose: () => void;
  onNotificationClick: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function NotificationPanel({ onClose, onNotificationClick }: NotificationPanelProps) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError } = useNotifications();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const dismissNotification = useDismissNotification();

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  // Handle click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Handle escape key to close
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  /**
   * Handle clicking on a notification
   */
  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      // Mark as read if not already
      if (!notification.isRead) {
        markRead.mutate(notification.id);
      }

      // Navigate to action URL if provided
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
        onNotificationClick();
      }
    },
    [markRead, navigate, onNotificationClick]
  );

  /**
   * Handle dismissing a notification
   */
  const handleDismiss = useCallback(
    (e: React.MouseEvent, notification: Notification) => {
      e.stopPropagation();
      dismissNotification.mutate(notification.id);
    },
    [dismissNotification]
  );

  /**
   * Handle marking all as read
   */
  const handleMarkAllRead = useCallback(() => {
    markAllRead.mutate();
  }, [markAllRead]);

  // Group notifications by date
  const groupedNotifications = groupByDate(notifications);
  const groupOrder = ['Today', 'Yesterday', 'Earlier'];

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[80vh] flex flex-col"
      role="dialog"
      aria-label="Notifications"
      aria-modal="true"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
        <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            disabled={markAllRead.isPending}
            className={cn(
              'flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700',
              'focus:outline-none focus:underline',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            aria-label="Mark all notifications as read"
          >
            <CheckDoubleIcon className="h-4 w-4" />
            <span>Mark all as read</span>
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center mb-3">
              <BellIcon className="h-6 w-6 text-red-500" />
            </div>
            <p className="text-sm text-gray-600">Failed to load notifications</p>
            <p className="text-xs text-gray-500 mt-1">Please try again later</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <BellIcon className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-600">No notifications yet</p>
            <p className="text-xs text-gray-500 mt-1">
              We&apos;ll notify you when something important happens
            </p>
          </div>
        ) : (
          <div className="py-2">
            {groupOrder.map((groupName) => {
              const group = groupedNotifications.get(groupName);
              if (!group || group.length === 0) return null;

              return (
                <div key={groupName}>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                    {groupName}
                  </div>
                  <ul role="list">
                    {group.map((notification) => (
                      <li key={notification.id}>
                        <button
                          onClick={() => handleNotificationClick(notification)}
                          className={cn(
                            'w-full flex items-start gap-3 px-4 py-3 text-left',
                            'hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
                            'transition-colors',
                            !notification.isRead && 'bg-blue-50/50'
                          )}
                          aria-label={`${notification.title}${notification.isRead ? '' : ' (unread)'}`}
                        >
                          <NotificationIcon type={notification.type} size="md" />

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p
                                className={cn(
                                  'text-sm',
                                  notification.isRead ? 'text-gray-900' : 'font-medium text-gray-900'
                                )}
                              >
                                {notification.title}
                              </p>

                              {/* Dismiss button */}
                              <button
                                onClick={(e) => handleDismiss(e, notification)}
                                disabled={dismissNotification.isPending}
                                className={cn(
                                  'flex-shrink-0 p-1 rounded-full',
                                  'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
                                  'focus:outline-none focus:ring-2 focus:ring-primary-500',
                                  'disabled:opacity-50'
                                )}
                                aria-label={`Dismiss notification: ${notification.title}`}
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>

                            {notification.message && (
                              <p className="mt-0.5 text-sm text-gray-600 line-clamp-2">
                                {notification.message}
                              </p>
                            )}

                            <p className="mt-1 text-xs text-gray-500">
                              {formatRelativeTime(notification.createdAt)}
                            </p>
                          </div>

                          {/* Unread indicator */}
                          {!notification.isRead && (
                            <div
                              className="flex-shrink-0 h-2 w-2 rounded-full bg-primary-500 mt-2"
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
